import type { Demanda } from '../models';
import { eventBus } from '../hooks/useEventBus';

interface CacheEntry {
  data: Demanda[];
  timestamp: number;
  ttl: number; // Time to live em milliseconds
}

interface CacheStats {
  hits: number;
  misses: number;
  lastUpdated: Record<string, number>;
}

class DemandasCacheService {
  private cache = new Map<string, CacheEntry>();
  private stats: CacheStats = {
    hits: 0,
    misses: 0,
    lastUpdated: {}
  };
  
  // TTL padrão de 5 minutos
  private readonly DEFAULT_TTL = 5 * 60 * 1000;
  
  // Listeners para mudanças no cache
  private listeners = new Set<(key: string, data: Demanda[]) => void>();

  constructor() {
    // Escuta eventos para invalidar cache automaticamente
    this.setupEventListeners();
  }

  /**
   * Configura listeners de eventos para invalidação automática do cache
   */
  private setupEventListeners() {
    // Quando setor muda, invalida todos os caches relacionados
    eventBus.on('sector-changed', () => {
      this.invalidateAll();
    });

    // Quando demanda é criada, invalida caches relevantes
    eventBus.on('demand-created', ({ sectorId }) => {
      this.invalidateByPatterns(['received', 'all', 'toAssign']);
      if (sectorId) {
        this.invalidateByPatterns([`sector-${sectorId}`]);
      }
    });

    // Quando demanda é atualizada, invalida caches relevantes
    eventBus.on('demand-updated', () => {
      this.invalidateByPatterns(['received', 'sent', 'watching', 'all']);
    });

    // Quando status de demanda muda, invalida caches
    eventBus.on('demand-status-changed', () => {
      this.invalidateByPatterns(['received', 'sent', 'watching', 'all', 'toAssign']);
    });

    // Quando demanda é atribuída, invalida caches
    eventBus.on('demand-assigned', () => {
      this.invalidateByPatterns(['received', 'toAssign', 'all']);
    });
  }

  /**
   * Adiciona um listener para mudanças no cache
   */
  addListener(callback: (key: string, data: Demanda[]) => void) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notifica todos os listeners sobre mudanças
   */
  private notifyListeners(key: string, data: Demanda[]) {
    this.listeners.forEach(callback => {
      try {
        callback(key, data);
      } catch (error) {
        console.warn('Erro ao notificar listener do cache:', error);
      }
    });
  }

  /**
   * Verifica se uma entrada do cache ainda é válida
   */
  private isValid(entry: CacheEntry): boolean {
    return Date.now() - entry.timestamp < entry.ttl;
  }

  /**
   * Obtém dados do cache se válidos
   */
  get(key: string): Demanda[] | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      this.stats.misses++;
      return null;
    }
    
    if (!this.isValid(entry)) {
      this.cache.delete(key);
      this.stats.misses++;
      return null;
    }
    
    this.stats.hits++;
    return [...entry.data]; // Retorna cópia para evitar mutações
  }

  /**
   * Armazena dados no cache
   */
  set(key: string, data: Demanda[], ttl: number = this.DEFAULT_TTL): void {
    const entry: CacheEntry = {
      data: [...data], // Armazena cópia para evitar mutações
      timestamp: Date.now(),
      ttl
    };
    
    this.cache.set(key, entry);
    this.stats.lastUpdated[key] = entry.timestamp;
    
    // Notifica listeners sobre a atualização
    this.notifyListeners(key, data);
  }

  /**
   * Remove uma entrada específica do cache
   */
  delete(key: string): boolean {
    const deleted = this.cache.delete(key);
    if (deleted) {
      delete this.stats.lastUpdated[key];
    }
    return deleted;
  }

  /**
   * Limpa todo o cache
   */
  clear(): void {
    this.cache.clear();
    this.stats = {
      hits: 0,
      misses: 0,
      lastUpdated: {}
    };
  }

  /**
   * Remove entradas expiradas do cache
   */
  cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    this.cache.forEach((entry, key) => {
      if (now - entry.timestamp >= entry.ttl) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      delete this.stats.lastUpdated[key];
    });
  }

  /**
   * Verifica se precisa atualizar uma entrada (está próxima do vencimento)
   */
  shouldRefresh(key: string, refreshThreshold: number = 0.8): boolean {
    const entry = this.cache.get(key);
    if (!entry) return true;
    
    const elapsed = Date.now() - entry.timestamp;
    const threshold = entry.ttl * refreshThreshold;
    
    return elapsed >= threshold;
  }

  /**
   * Obtém o tempo restante de vida de uma entrada (em ms)
   */
  getTimeToLive(key: string): number {
    const entry = this.cache.get(key);
    if (!entry) return 0;
    
    const elapsed = Date.now() - entry.timestamp;
    return Math.max(0, entry.ttl - elapsed);
  }

  /**
   * Obtém estatísticas do cache
   */
  getStats(): CacheStats & { 
    size: number; 
    hitRate: number;
    entries: Array<{ key: string; age: number; ttl: number; size: number }>;
  } {
    const total = this.stats.hits + this.stats.misses;
    const hitRate = total > 0 ? this.stats.hits / total : 0;
    
    const entries = Array.from(this.cache.entries()).map(([key, entry]) => ({
      key,
      age: Date.now() - entry.timestamp,
      ttl: entry.ttl,
      size: entry.data.length
    }));
    
    return {
      ...this.stats,
      size: this.cache.size,
      hitRate,
      entries
    };
  }

  /**
   * Força a atualização de uma entrada específica
   */
  invalidate(key: string): void {
    this.delete(key);
  }

  /**
   * Invalida entradas baseadas em padrões de chave
   */
  invalidateByPatterns(patterns: string[]): void {
    const keysToDelete: string[] = [];
    
    this.cache.forEach((_, key) => {
      for (const pattern of patterns) {
        if (key.includes(pattern)) {
          keysToDelete.push(key);
          break;
        }
      }
    });
    
    keysToDelete.forEach(key => this.delete(key));
    
    if (keysToDelete.length > 0) {
      // Notifica sobre invalidação
      eventBus.emit('cache-invalidated', { cacheKeys: keysToDelete });
    }
  }

  /**
   * Força a atualização de todas as entradas relacionadas a demandas
   */
  invalidateAll(): void {
    const allKeys = this.getKeys();
    this.clear();
    
    if (allKeys.length > 0) {
      eventBus.emit('cache-invalidated', { cacheKeys: allKeys });
    }
  }

  /**
   * Obtém todas as chaves do cache
   */
  getKeys(): string[] {
    return Array.from(this.cache.keys());
  }
}

// Instância singleton
export const demandasCache = new DemandasCacheService();

// Limpeza automática a cada 10 minutos
setInterval(() => {
  demandasCache.cleanup();
}, 10 * 60 * 1000);
